import React, { useState, useEffect, useContext } from 'react';
import { Form, Input, Select, Button, message, Table, InputNumber, Card, Divider, Typography, Space } from 'antd';
import axios from 'axios';
import { LoginContext } from '../../context/loginContext';
import ArticulosTable from './articulosTable.js';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;
const { Title } = Typography;

const CEntrada = () => {
    const [usuarios, setUsuarios] = useState([]);
    const [articulos, setArticulos] = useState([]);
    const [selectedUsuario, setSelectedUsuario] = useState(null);
    const [selectedArticulos, setSelectedArticulos] = useState([]);
    const [letra, setLetra] = useState(null);
    const [num1, setNum1] = useState(null);
    const [num2, setNum2] = useState(null);
    const [form] = Form.useForm();
    const { user } = useContext(LoginContext);
    const [posicion, setPosicion] = useState(null);
    const [isComponentVisible, setIsComponentVisible] = useState(false);
    const [searchText, setSearchText] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        fetchUsuarios();
        fetchArticulos();
    }, []);

    const fetchUsuarios = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/usuarios/clientes`, {
                headers: { Authorization: `Bearer ${user.token}` },
            });
            setUsuarios(response.data);
        } catch (error) {
            message.error('Error al obtener usuarios');
        }
    };

    const fetchArticulos = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/articulos`, {
                headers: { Authorization: `Bearer ${user.token}` },
            });
            setArticulos(response.data);
        } catch (error) {
            message.error('Error al obtener artículos');
        }
    };

    const handleSearchChange = (e) => {
        setSearchText(e.target.value.toLowerCase());
    };

    const filteredUsuarios = usuarios.filter((usuario) =>
        usuario.usuario.toLowerCase().includes(searchText)
    );

    const onFinish = async (values) => {
        try {
            const generatedPosicion = `${values.letra}${values.num1}${values.num2}`;
            const dataToSend = {
                idUsuario: values.usuario,
                posicion: generatedPosicion,
                tipoMovimiento: 'Entrada',
                articulos: selectedArticulos.map(art => ({ articulo: art._id, cantidad: art.cantidad })),
                quienRetira: values.quienRetira,
            };

            const headers = {
                Authorization: `Bearer ${user.token}`,
            };

            await axios.post(`${process.env.REACT_APP_API_URL}/movimientos`, dataToSend, { headers });
            message.success('Movimiento guardado con éxito');
            form.resetFields();
            setSelectedArticulos([]);
            navigate('/posiciones');
        } catch (error) {
            message.error('Error al enviar el formulario');
        }
    };

    const handleCantidadChange = (value, articuloId) => {
        const updatedArticulos = selectedArticulos.map(art =>
            art._id === articuloId ? { ...art, cantidad: value } : art
        );
        setSelectedArticulos(updatedArticulos);
    };

    const articuloColumns = [
        { title: 'Artículo', dataIndex: 'nombre', key: 'nombre' },
        {
            title: 'Marca',
            dataIndex: 'idmarca',
            key: 'idmarca',
            render: (idmarca) => idmarca ? idmarca.nombre : 'N/A',
        },
        {
            title: 'Categoría',
            dataIndex: 'idcategoria',
            key: 'idcategoria',
            render: (idcategoria) => idcategoria ? idcategoria.nombre : 'N/A',
        },
        { title: 'Unidad de Medida', dataIndex: 'unidadDeMedida', key: 'unidadDeMedida' },
    ];

    const userColumns = [
        {
            title: 'Usuario',
            dataIndex: 'usuario',
            key: 'usuario',
            filterDropdown: () => (
                <Input
                    placeholder="Buscar usuario"
                    value={searchText}
                    onChange={handleSearchChange}
                    style={{ marginBottom: 8, display: 'block' }}
                />
            ),
            filterIcon: () => <Button type="link">Buscar</Button>,
        },
        { title: 'Email', dataIndex: 'email', key: 'email' },
        { title: 'Celular', dataIndex: 'celular', key: 'celular' },
        { title: 'CUIT', dataIndex: 'cuit', key: 'cuit' },
        { title: 'Dirección', dataIndex: 'direccion', key: 'direccion' },
        { title: 'Tipo de Usuario', dataIndex: 'tipoUsuario', key: 'tipoUsuario' },
    ];

    const rowSelection = {
        type: 'radio',
        onChange: (selectedRowKeys, selectedRows) => {
            setSelectedUsuario(selectedRows[0]);
            form.setFieldsValue({ usuario: selectedRows[0]._id });
        },
    };

    const handleArticuloSelection = (selectedRowKeys, selectedRows) => {
        const articulosConCantidad = selectedRows.map(art => ({ ...art, cantidad: 1 }));
        setSelectedArticulos(articulosConCantidad);
    };

    const onPositionChange = async (letraVal, num1Val, num2Val) => {
        if (letraVal && num1Val && num2Val) {
            const generatedPosicion = `${letraVal}${num1Val}${num2Val}`;
            setPosicion(generatedPosicion);
            setIsComponentVisible(false);
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/movimientos/${generatedPosicion}/consulta`, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });

                if (response.data) {
                    setIsComponentVisible(true);
                }
            } catch (error) {
                if (error.response && error.response.status === 404) {
                    message.info('No se encontró ningún movimiento para esta posición, puedes continuar.');
                } else {
                    message.error('Error al verificar la posición.');
                }
            }
        }
    };

    const handleLetraChange = (value) => {
        setLetra(value);
        onPositionChange(value, num1, num2);
    };

    const handleNum1Change = (value) => {
        setNum1(value);
        onPositionChange(letra, value, num2);
    };

    const handleNum2Change = (value) => {
        setNum2(value);
        onPositionChange(letra, num1, value);
    };

    return (
        <Card bordered={false}>
            <Title level={3}>Registrar Movimiento de Entrada</Title>
            <Divider />
            <Form form={form} onFinish={onFinish} layout="vertical">
                <Form.Item label="Posición" required>
                    <Space>
                        <Form.Item
                            name="letra"
                            noStyle
                            rules={[{ required: true, message: 'Selecciona una letra' }]}>
                            <Select placeholder="Letra" style={{ width: 100 }} onChange={handleLetraChange}>
                                {Array.from({ length: 26 }, (_, i) => (
                                    <Option key={i} value={String.fromCharCode(65 + i)}>
                                        {String.fromCharCode(65 + i)}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            name="num1"
                            noStyle
                            rules={[{ required: true, message: 'Selecciona un número' }]}>
                            <Select placeholder="1-20" style={{ width: 100 }} onChange={handleNum1Change}>
                                {Array.from({ length: 20 }, (_, i) => (
                                    <Option key={i + 1} value={i + 1}>
                                        {i + 1}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            name="num2"
                            noStyle
                            rules={[{ required: true, message: 'Selecciona un número' }]}>
                            <Select placeholder="1-20" style={{ width: 100 }} onChange={handleNum2Change}>
                                {Array.from({ length: 20 }, (_, i) => (
                                    <Option key={i + 1} value={i + 1}>
                                        {i + 1}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Space>
                </Form.Item>
                <Divider />
                <Form.Item
                    name="usuario"
                    label="Selecciona un Usuario"
                    rules={[{ required: true, message: 'Selecciona un usuario' }]}>
                    <Table
                        rowSelection={rowSelection}
                        columns={userColumns}
                        dataSource={filteredUsuarios}
                        pagination={{ pageSize: 5 }}
                        rowKey="_id"
                        style={{ marginTop: 16 }}
                    />
                </Form.Item>
                <Divider />
                <Title level={4}>Selecciona los Artículos</Title>
                <Table
                    rowSelection={{
                        type: 'checkbox',
                        onChange: handleArticuloSelection,
                    }}
                    columns={articuloColumns}
                    dataSource={articulos}
                    pagination={{ pageSize: 5 }}
                    rowKey="_id"
                    style={{ marginTop: 16 }}
                />
                <Divider />
                <Title level={4}>Cantidad por Artículo</Title>
                {selectedArticulos.map((art) => (
                    <Form.Item key={art._id} label={art.nombre}>
                        <InputNumber
                            min={1}
                            value={art.cantidad}
                            onChange={(value) => handleCantidadChange(value, art._id)}
                        />
                    </Form.Item>
                ))}
                <Divider />
                <Form.Item
                    name="quienRetira"
                    label="Quién Retira"
                    rules={[{ required: true, message: 'Introduce el nombre de la persona que retira' }]}>
                    <Input />
                </Form.Item>
                <Form.Item>
                    <Button type="primary" htmlType="submit">
                        Registrar Movimiento
                    </Button>
                </Form.Item>
            </Form>
            {isComponentVisible && <ArticulosTable posicion={posicion} />}
        </Card>
    );
};

export default CEntrada;
